import * as React from 'react';
import classnames from 'classnames';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import InstagramDataEnabledBadge from 'components/instagram-data-enabled/badge/InstagramDataEnabledBadge';

import { __t, applyTranslations } from 'utils/language';
import { sanitizeGenericHtml } from 'utils/sanitize';
import { getItem, setItem } from 'utils/storage/localStorage';
import {
  CONTENT_METRICS_BANNER_TOGGLE_STATE_STORAGE_KEY
} from '../utils/metrics-temp-fb';

applyTranslations(require('./metrics-temp-fb-banner.i18n.json'));

import './metrics-temp-fb-banner.scss';

const MSG_KEY = 'metrics_temp_fb_banner';

const MetricsTempFBBanner: React.FC = () => {
  const savedToggleState = getItem(CONTENT_METRICS_BANNER_TOGGLE_STATE_STORAGE_KEY);
  const [tileSize, setTileSize] = React.useState(savedToggleState || 'expanded');

  const bodyText = __t(`${MSG_KEY}.body`);
  const learnMoreText = __t(`${MSG_KEY}.disclaimers.learn_more`, {
    helpPage: 'https://help.collabary.com/en/articles/2805459-where-does-collabary-source-audience-data-and-statistics'
  });

  const handleToggle = (): void => {
    const updatedValue = (savedToggleState || tileSize) === 'expanded' ? 'collapsed' : 'expanded';

    setTileSize(updatedValue);
    setItem(CONTENT_METRICS_BANNER_TOGGLE_STATE_STORAGE_KEY, updatedValue);
  };

  const renderBody = (): React.ReactNode => {
    const bodyClasses = classnames('metrics-temp-fb-banner__body', {
      [`metrics-temp-fb-banner__body--size-${tileSize}`]: Boolean(tileSize)
    });

    return (
      <div
        className={bodyClasses}
        dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(bodyText) }}
      />
    );
  };

  const renderDisclaimer = (): React.ReactNode => {
    const disclaimerClasses = classnames('metrics-temp-fb-banner__disclaimer', {
      [`metrics-temp-fb-banner__disclaimer--size-${tileSize}`]: Boolean(tileSize)
    });

    return (
      <div className={disclaimerClasses}>
        <div>
          {__t(`${MSG_KEY}.disclaimers.applicable`)}
        </div>&nbsp;
        <div dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(learnMoreText) }} />
      </div>
    );
  };

  const renderChevron = (): React.ReactNode => {
    const chevronClasses = classnames('metrics-temp-fb-banner__chevron', {
      [`metrics-temp-fb-banner__chevron--size-${tileSize}`]: Boolean(tileSize)
    });

    return (
      <div
        className={chevronClasses}
        onClick={handleToggle}
      >
        <SVGDynamicIcon
          name="chevron-down-custom-fg"
          width={15}
          height={8}
          customColor="grey"
          clickable
        />
      </div>
    );
  };

  const renderInstagramBadge = (): React.ReactNode => (
    <div className="metrics-temp-fb-banner__badge">
      <InstagramDataEnabledBadge
        hasActiveFbConnection
        influencerCheckOverride
      />
    </div>
  );

  const containerClasses = classnames('metrics-temp-fb-banner', {
    [`metrics-temp-fb-banner--size-${tileSize}`]: Boolean(tileSize)
  });

  return (
    <div className={containerClasses}>
      <div className="metrics-temp-fb-banner__headline">
        {__t(`${MSG_KEY}.headline`)}
      </div>
      {renderBody()}
      {renderDisclaimer()}
      {renderChevron()}
      {renderInstagramBadge()}
    </div>
  );
};

export default MetricsTempFBBanner;
